import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDownload } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { VaultResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './CloseAccountPage.css';

export default function CloseAccountPage() {
  const { vaultId } = useParams<{ vaultId: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [vault, setVault] = useState<VaultResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [downloading, setDownloading] = useState(false);

  // Step 2
  const [acknowledged, setAcknowledged] = useState(false);

  // Step 3
  const [nameInput, setNameInput] = useState('');
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!vaultId) return;
    apiGet<VaultResponse>(`/api/vault/${vaultId}`)
      .then(setVault)
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [vaultId, navigate]);

  async function handleDownload() {
    setDownloading(true);
    try {
      const { blob, fileName } = await apiDownload(`/api/vault/${vaultId}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Best-effort — don't block the user from continuing
    } finally {
      setDownloading(false);
      setStep(2);
    }
  }

  async function handleClose() {
    if (!vault || !vaultId) return;
    setClosing(true);
    setError('');
    try {
      await apiPost(`/api/vault/${vaultId}/close`, { vaultNameConfirmation: nameInput });
      await logout();
      navigate('/');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setClosing(false);
    }
  }

  const nameMatches = vault
    ? nameInput.trim().toLowerCase() === vault.name.trim().toLowerCase()
    : false;

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container close-account-container">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!vault) return null;

  return (
    <div className="auth-page">
      <div className="auth-container close-account-container">
        <p className="close-account-step-indicator">Step {step} of 3</p>

        {step === 1 && (
          <div className="close-account-step">
            <h1 className="auth-title">Before you go</h1>
            <p className="close-account-body">
              Download a copy of your vault so these memories are never lost.
            </p>
            <button
              className="btn-primary close-account-download-btn"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? <LoadingSpinner small /> : 'Export Vault (ZIP)'}
            </button>
            <button
              className="btn-text close-account-skip"
              onClick={() => setStep(2)}
              disabled={downloading}
            >
              Skip this step →
            </button>
            <button
              className="btn-text close-account-cancel"
              onClick={() => navigate('/')}
              disabled={downloading}
            >
              Cancel — keep my account
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="close-account-step">
            <h1 className="auth-title">Are you sure?</h1>
            <label className="close-account-checkbox-label">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={e => setAcknowledged(e.target.checked)}
              />
              <span>
                I have saved a copy of my vault, or I understand this data will be permanently and irreversibly deleted.
              </span>
            </label>
            <button
              className="btn-primary"
              onClick={() => setStep(3)}
              disabled={!acknowledged}
            >
              Continue →
            </button>
            <button
              className="btn-text close-account-back"
              onClick={() => setStep(1)}
            >
              ← Back
            </button>
            <button
              className="btn-text close-account-cancel"
              onClick={() => navigate('/')}
            >
              Cancel — keep my account
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="close-account-step">
            <h1 className="auth-title">Confirm closure</h1>
            <p className="close-account-body">
              Type the name of your vault to confirm:
            </p>
            <p className="close-account-vault-name">{vault.name}</p>
            <input
              className="form-input"
              type="text"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={vault.name}
              autoFocus
            />
            <AlertMessage type="error" message={error} />
            <button
              className="btn-danger"
              onClick={handleClose}
              disabled={!nameMatches || closing}
            >
              {closing ? <LoadingSpinner small /> : 'Close My Account'}
            </button>
            <button
              className="btn-text close-account-back"
              onClick={() => { setStep(2); setError(''); setNameInput(''); }}
              disabled={closing}
            >
              ← Back
            </button>
            <button
              className="btn-text close-account-cancel"
              onClick={() => navigate('/')}
              disabled={closing}
            >
              Cancel — keep my account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
